import { inject, injectable } from "inversify";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { makeObservable, computed } from "mobx";

export enum TAB_NAMES {
  EngagementInsights = "engagement_insights",
  CallRecordingsInsights = "call_recordings_insights",
  UserAnalytics = "user_analytics",
  UsersConversationsStatus = "users_conversations_status",
}

export const TAB_URL_PATHS: Record<TAB_NAMES, string> = {
  [TAB_NAMES.UserAnalytics]: "user-analytics",
  [TAB_NAMES.UsersConversationsStatus]: "users-conversations-status",
  [TAB_NAMES.EngagementInsights]: "conversations",
  [TAB_NAMES.CallRecordingsInsights]: "call-recordings",
};

const URL_PATH_TO_TAB = Object.fromEntries(Object.entries(TAB_URL_PATHS).map(([tab, path]) => [path, tab as TAB_NAMES])) as Record<string, TAB_NAMES>;
const DASHBOARD_BASE_PATH = "/analytics/dashboard";

export const tabNameFromPathname = (pathname: string): TAB_NAMES | undefined => {
  const segment = pathname.split("/").filter(Boolean).pop();
  return segment ? URL_PATH_TO_TAB[segment] : undefined;
};

export const dashboardTabPath = (tab: TAB_NAMES, search = ""): string => {
  return `${DASHBOARD_BASE_PATH}/${TAB_URL_PATHS[tab]}${search}`;
};

const configKeys = [
  "allow_mobile_call_recording",
  "track_filter_analytics",
  "allow_user_analytics",
  "allow_twilio",
  "restrict_to_direct_reports",
  "restrict_to_direct_reports_bypass_list",
] as const;

@injectable()
export class AnalyticsDashboardStore {
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore
  ) {
    makeObservable(this, {
      orgConfigs: computed,
      isLoading: computed,
      tabPermissions: computed,
      defaultTab: computed,
    });
  }

  get orgConfigs() {
    return this.orgConfigsStore.getObservableConfigs(configKeys);
  }

  get isLoading() {
    return this.orgConfigs.state === "pending";
  }

  get tabPermissions() {
    if (this.orgConfigs.state !== "fulfilled") return;
    return {
      [TAB_NAMES.EngagementInsights]: hasPermission(this.userStore.user, UserPermissions["analytics.view"]),
      [TAB_NAMES.CallRecordingsInsights]: Boolean(this.orgConfigs.value.allow_mobile_call_recording || this.orgConfigs.value.allow_twilio),
      [TAB_NAMES.UserAnalytics]: Boolean(this.orgConfigs.value.allow_user_analytics),
      [TAB_NAMES.UsersConversationsStatus]: Boolean(
        this.orgConfigs.value.track_filter_analytics && hasPermission(this.userStore.user, UserPermissions["analytics.view"])
      ),
    };
  }

  get defaultTab() {
    if (!this.tabPermissions) {
      return TAB_NAMES.EngagementInsights;
    }

    if (this.tabPermissions[TAB_NAMES.UserAnalytics]) {
      return TAB_NAMES.UserAnalytics;
    }

    return Object.keys(this.tabPermissions).find((key) => this.tabPermissions?.[key as TAB_NAMES]) as TAB_NAMES | undefined;
  }
}
